import { useCallback, useEffect, useRef, useState } from 'react';

import { type BriefSynthesisView, fetchBrief, retryBrief } from '../api/client.ts';

/**
 * The study's latest request to write its Brief, as the Analyzing step reads it: one read that the
 * analysing headline, the synthesis status line and the Continue hint are all decided from.
 */
export interface BriefSynthesisRead {
  /** Whether a read for this study has landed. Until it has, nothing is known about a request. */
  loaded: boolean;
  /** The request, or `null` where the study has none yet. Always `null` until `loaded`. */
  synthesis: BriefSynthesisView | null;
  /** Whether Try again's request is out. */
  busy: boolean;
  /** Whether Try again was refused for the request on screen. */
  retryFailed: boolean;
  /** Try again: a new request over the same evidence. */
  retry: () => Promise<void>;
}

/**
 * Reads the Brief request on every `refreshToken` while `active` — an `analyzing` study whose
 * analysis has not been given up on, on a step that shows the request — and holds nothing while it
 * is not, so a request read before an escalation is never what the step shows after it is
 * withdrawn.
 *
 * A failed read keeps the last request rather than withdrawing it: the next tick asks again, and a
 * line that came and went with each blip would be noise over a request that has not changed.
 */
export const useBriefSynthesis = ({
  studyId,
  refreshToken,
  active,
  onStudyChanged,
}: {
  studyId: string;
  refreshToken: number;
  active: boolean;
  /** Asks the shell to read the study again, after Try again has been answered either way. */
  onStudyChanged: () => void;
}): BriefSynthesisRead => {
  const [read, setRead] = useState<{
    studyId: string;
    synthesis: BriefSynthesisView | null;
  } | null>(null);
  /** The study whose Try again is out, so a request still out for one study is not busy for another. */
  const [busyFor, setBusyFor] = useState<string | null>(null);
  const [retryFailed, setRetryFailed] = useState(false);
  /**
   * Moved on as Try again is answered, so a read that was out across it is dropped when it lands.
   * Such a read may have been answered before the retry was stored, and showing it would put the
   * old failure back over the request Try again just made. One landing before the answer is
   * harmless: the answer replaces whatever it showed.
   */
  const epoch = useRef(0);
  /**
   * Moved on whenever the study or `active` changes, so a Try again answered after either is
   * dropped: its request belongs to a read the hook has since let go of.
   */
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
  }, [studyId, active]);

  useEffect(() => {
    if (!active) {
      setRead(null);
      return undefined;
    }

    const controller = new AbortController();
    const asked = epoch.current;

    fetchBrief(studyId, controller.signal).then(
      (answer) => {
        if (!controller.signal.aborted && asked === epoch.current) {
          setRead({ studyId, synthesis: answer.synthesis });
        }
      },
      () => undefined,
    );

    return () => controller.abort();
  }, [studyId, refreshToken, active]);

  const current = active && read?.studyId === studyId ? read : null;
  const loaded = current !== null;
  const synthesis = current?.synthesis ?? null;
  /** Which request is on screen: a refusal of Try again is about that request and no later one. */
  const shown = synthesis === null ? null : `${synthesis.requestedAt}#${String(synthesis.attempt)}`;

  useEffect(() => {
    setRetryFailed(false);
  }, [shown]);

  const onStudyChangedRef = useRef(onStudyChanged);
  useEffect(() => {
    onStudyChangedRef.current = onStudyChanged;
  });

  /*
    The answer is the request it stored, shown at once; a refusal — another tab already asked, or
    the study moved on — is said, and the study is read again so the screen catches up with
    whatever did happen.
  */
  const retry = useCallback(async () => {
    const asked = generation.current;
    setBusyFor(studyId);
    setRetryFailed(false);
    try {
      const answer = await retryBrief(studyId);
      if (answer.synthesis !== null && asked === generation.current) {
        setRead({ studyId, synthesis: answer.synthesis });
      }
    } catch {
      if (asked === generation.current) {
        setRetryFailed(true);
      }
    } finally {
      epoch.current += 1;
      setBusyFor((asking) => (asking === studyId ? null : asking));
      onStudyChangedRef.current();
    }
  }, [studyId]);

  const busy = busyFor === studyId;

  return { loaded, synthesis, busy, retryFailed, retry };
};
